import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Shadow, Spacing, Typography } from '../../theme';
import { formatLastSeen, formatMemberCount } from '../../utils/chatFormatting';
import ChatAvatar from './ChatAvatar';
import type { UserPresence } from '../../types';
import { useI18n } from '../../i18n';
import { useTheme } from '../../theme/useTheme';
import type { TonyteTheme } from '../../theme/themes';
import { daylightStyle } from '../../theme/daylightStyle';

interface Props {
  name: string;
  photo?: string;
  isGroupChat: boolean;
  memberCount: number;
  isVerified?: boolean;
  presence?: UserPresence | null;
  isOtherUserTyping?: boolean;
  eventOrPlanContext?: string; // e.g. "At Event: Club XYZ" / "Exploring Dresden"
  onPressBack: () => void;
  onPressCenter: () => void;
  onPressOptions: () => void;
  centerDisabled?: boolean;
  hideBack?: boolean;
}

function AnimatedStatusLine({ text, tone }: { text: string; tone: 'muted' | 'online' | 'typing' }) {
  const { theme } = useTheme();
  const fade = useRef(new Animated.Value(0)).current;
  const lastTextRef = useRef(text);

  useEffect(() => {
    if (lastTextRef.current === text) return;
    lastTextRef.current = text;
    fade.setValue(0);
    Animated.timing(fade, { toValue: 1, duration: 220, useNativeDriver: true }).start();
  }, [fade, text]);

  useEffect(() => {
    Animated.timing(fade, { toValue: 1, duration: 220, useNativeDriver: true }).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const color = tone === 'online' ? theme.colors.success : tone === 'typing' ? theme.colors.accentSecondary : (theme.isDark ? 'rgba(255,255,255,0.5)' : theme.colors.textMuted);

  return (
    <Animated.Text
      style={[styles.headerSub, { color, opacity: fade }]}
      numberOfLines={1}
    >
      {text}
    </Animated.Text>
  );
}

const ChatHeader: React.FC<Props> = ({
  name,
  photo,
  isGroupChat,
  memberCount,
  isVerified,
  presence,
  isOtherUserTyping,
  eventOrPlanContext,
  onPressBack,
  onPressCenter,
  onPressOptions,
  centerDisabled,
  hideBack,
}) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createAppearanceStyles(theme), [theme]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  let statusText = '';
  let statusTone: 'muted' | 'online' | 'typing' = 'muted';

  if (isGroupChat) {
    statusText = eventOrPlanContext || formatMemberCount(memberCount);
  } else if (isOtherUserTyping) {
    statusText = t('chat.typing');
    statusTone = 'typing';
  } else if (presence?.isOnline) {
    statusText = t('chat.online');
    statusTone = 'online';
  } else if (presence?.lastActiveAt) {
    statusText = formatLastSeen(presence.lastActiveAt);
  }
  void now; // forces the 30s re-render so "Last seen Xm ago" stays fresh

  return (
    <View style={[styles.wrap, appearance.wrap]}>
      <LinearGradient
        pointerEvents="none"
        colors={theme.isDark
          ? ['rgba(255,255,255,0.105)', 'rgba(255,255,255,0.032)', 'rgba(255,255,255,0.012)']
          : theme.gradients.navBase}
        locations={[0, 0.46, 1]}
        start={{ x: 0.1, y: 0 }}
        end={{ x: 0.9, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      {!theme.isDark ? (
        <LinearGradient
          pointerEvents="none"
          colors={theme.gradients.navGlass}
          locations={[0, 0.5, 1]}
          start={{ x: 0.08, y: 0 }}
          end={{ x: 0.76, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
      ) : null}
      <LinearGradient
        pointerEvents="none"
        colors={theme.isDark
          ? ['rgba(242,125,38,0.035)', 'rgba(124,109,255,0.024)', 'rgba(0,0,0,0)']
          : ['rgba(242,125,38,0.08)', 'rgba(21,155,154,0.025)', 'rgba(255,255,255,0)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      {hideBack ? <View style={styles.backBtn} /> : <TouchableOpacity
        style={[styles.backBtn, appearance.iconButton]}
        onPress={onPressBack}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        activeOpacity={0.8}
      >
        {!theme.isDark ? (
          <LinearGradient
            pointerEvents="none"
            colors={theme.gradients.navButtonInactive}
            start={{ x: 0.2, y: 0 }}
            end={{ x: 0.8, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
        ) : null}
        <Icon name="x" size={20} color={theme.isDark ? Colors.textPrimary : theme.colors.accent} />
      </TouchableOpacity>}

      <TouchableOpacity
        style={styles.center}
        onPress={onPressCenter}
        disabled={centerDisabled}
        activeOpacity={0.85}
      >
        <ChatAvatar uri={photo} size={48} isOnline={!isGroupChat && presence?.isOnline} isVerified={!isGroupChat && isVerified} />
        <View style={styles.textBlock}>
          <Text style={[styles.headerName, appearance.headerName]} numberOfLines={1}>{name}</Text>
          {statusText ? <AnimatedStatusLine text={statusText} tone={statusTone} /> : null}
        </View>
      </TouchableOpacity>

      <TouchableOpacity style={[styles.optionsBtn, appearance.iconButton]} onPress={onPressOptions} activeOpacity={0.8}>
        {!theme.isDark ? (
          <LinearGradient
            pointerEvents="none"
            colors={theme.gradients.navButtonInactive}
            start={{ x: 0.2, y: 0 }}
            end={{ x: 0.8, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
        ) : null}
        <Icon name={isGroupChat ? 'info' : 'more-vertical'} size={19} color={theme.isDark ? 'rgba(255,255,255,0.7)' : theme.colors.iconSubtle} />
      </TouchableOpacity>
    </View>
  );
};

export default ChatHeader;

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    paddingHorizontal: Spacing.base,
    paddingBottom: Spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.075)',
    backgroundColor: 'rgba(6,10,18,0.58)',
    ...Shadow.card,
    shadowColor: '#000',
    shadowOpacity: 0.28,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 10,
    overflow: 'hidden',
  },
  backBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
  },
  center: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    minWidth: 0,
  },
  textBlock: {
    flex: 1,
    minWidth: 0,
  },
  headerName: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.extrabold,
    lineHeight: 22,
  },
  headerSub: {
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
    marginTop: 1,
  },
  optionsBtn: {
    width: 40,
    height: 40,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.15)',
    overflow: 'hidden',
  },
});

const createAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  wrap: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceGlass,
    borderBottomColor: theme.colors.borderStrong,
    ...theme.shadows.elevated,
  }),
  iconButton: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.borderStrong,
    ...theme.shadows.card,
  }),
  headerName: daylightStyle(theme, {
    color: theme.colors.textPrimary,
  }),
});
